import React, { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { userCan } from "../lib/api";
import {
    LayoutDashboard,
    Radio,
    Route,
    Users,
    Car,
    TrendingUp,
    Settings2,
    BadgePercent,
    LifeBuoy,
    UsersRound,
    LogOut,
    Menu,
    X,
} from "lucide-react";

const NAV = [
    { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, section: "dashboard" },
    { to: "/live", label: "Live Rides", icon: Radio, section: "live" },
    { to: "/rides", label: "All Rides", icon: Route, section: "rides" },
    { to: "/customers", label: "Customers", icon: Users, section: "customers" },
    { to: "/drivers", label: "Drivers", icon: Car, section: "drivers" },
    { to: "/earnings", label: "Earnings", icon: TrendingUp, section: "earnings" },
    { to: "/fare-rules", label: "Fare Rules", icon: Settings2, section: "fare-rules" },
    { to: "/promo-codes", label: "Promo Codes", icon: BadgePercent, section: "promo-codes" },
    { to: "/tickets", label: "Support", icon: LifeBuoy, section: "tickets" },
    { to: "/team", label: "Team", icon: UsersRound, section: "team" },
];

const Sidebar = ({ onClose, user }) => {
    const items = NAV.filter((n) => userCan(user, n.section));
    return (
        <aside className="h-full w-64 bg-white border-r border-slate-200 flex flex-col">
            <div className="px-6 py-5 border-b border-slate-200 flex items-center justify-between">
                <Link to="/dashboard" className="flex items-center gap-2" data-testid="brand-link">
                    <div className="w-9 h-9 rounded-xl bg-mint-500 flex items-center justify-center font-display font-bold text-white text-lg">Y</div>
                    <div>
                        <div className="font-display font-bold text-navy-900 text-lg leading-tight">YuGo</div>
                        <div className="text-[10px] tracking-[0.2em] text-mint-600 font-semibold">MUSAFIR</div>
                    </div>
                </Link>
                {onClose && (
                    <button onClick={onClose} className="lg:hidden" data-testid="sidebar-close-btn">
                        <X className="w-5 h-5 text-slate-600" />
                    </button>
                )}
            </div>
            <nav className="flex-1 overflow-y-auto py-4 px-3 space-y-1">
                {items.map((item) => {
                    const Icon = item.icon;
                    return (
                        <NavLink
                            key={item.to}
                            to={item.to}
                            data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                            className={({ isActive }) =>
                                `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                                    isActive
                                        ? "bg-mint-50 text-mint-700"
                                        : "text-slate-600 hover:bg-slate-50 hover:text-navy-900"
                                }`
                            }
                            onClick={onClose}
                        >
                            <Icon className="w-[18px] h-[18px]" />
                            <span>{item.label}</span>
                        </NavLink>
                    );
                })}
            </nav>
            <div className="border-t border-slate-200 px-4 py-3 text-[11px] text-slate-400">
                YuGo Admin · v1.0
            </div>
        </aside>
    );
};

const Topbar = ({ onOpenSidebar }) => {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const [open, setOpen] = useState(false);

    const handleLogout = async () => {
        await logout();
        navigate("/login");
    };

    return (
        <header className="h-16 bg-white border-b border-slate-200 px-4 lg:px-8 flex items-center justify-between sticky top-0 z-20">
            <div className="flex items-center gap-3">
                <button onClick={onOpenSidebar} className="lg:hidden" data-testid="topbar-menu-btn">
                    <Menu className="w-6 h-6 text-navy-900" />
                </button>
                <div className="font-display font-semibold text-navy-900 text-base hidden sm:block">
                    Operations Console
                </div>
            </div>
            <div className="relative">
                <button
                    onClick={() => setOpen(!open)}
                    className="flex items-center gap-2.5 px-2 py-1.5 rounded-full hover:bg-slate-50 transition-colors"
                    data-testid="user-menu-btn"
                >
                    <div className="w-9 h-9 rounded-full bg-gradient-to-br from-mint-400 to-mint-600 flex items-center justify-center text-white font-semibold">
                        {user?.name?.[0]?.toUpperCase() || "A"}
                    </div>
                    <div className="hidden md:block text-left">
                        <div className="text-sm font-semibold text-navy-900 leading-tight">{user?.name}</div>
                        <div className="text-[11px] text-slate-500 capitalize">{user?.role?.replace("_", " ")}</div>
                    </div>
                </button>
                {open && (
                    <div className="absolute right-0 mt-2 w-56 bg-white rounded-xl border border-slate-200 shadow-lg overflow-hidden" data-testid="user-menu-dropdown">
                        <div className="px-4 py-3 border-b border-slate-100">
                            <div className="text-sm font-semibold text-navy-900">{user?.name}</div>
                            <div className="text-xs text-slate-500 truncate">{user?.email}</div>
                        </div>
                        <button
                            onClick={handleLogout}
                            className="w-full text-left px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
                            data-testid="logout-btn"
                        >
                            <LogOut className="w-4 h-4" />
                            Logout
                        </button>
                    </div>
                )}
            </div>
        </header>
    );
};

const Layout = ({ children }) => {
    const [mobileOpen, setMobileOpen] = useState(false);
    const { user } = useAuth();
    return (
        <div className="min-h-screen bg-slate-50 flex">
            <div className="hidden lg:block w-64 shrink-0">
                <div className="fixed inset-y-0 left-0 w-64">
                    <Sidebar user={user} />
                </div>
            </div>

            {mobileOpen && (
                <div className="lg:hidden fixed inset-0 z-40">
                    <div className="absolute inset-0 bg-black/40" onClick={() => setMobileOpen(false)} />
                    <div className="absolute inset-y-0 left-0 z-50">
                        <Sidebar onClose={() => setMobileOpen(false)} user={user} />
                    </div>
                </div>
            )}

            <div className="flex-1 min-w-0">
                <Topbar onOpenSidebar={() => setMobileOpen(true)} />
                <main className="px-4 lg:px-8 py-6 lg:py-8 max-w-[1600px] mx-auto" data-testid="main-content">
                    {children}
                </main>
            </div>
        </div>
    );
};

export default Layout;
